// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Datasource configuration section for analysis nodes.
 *
 * Behavior depends on allowedTypes:
 * - Single allowed type: dropdown hidden, type always active
 * - Multiple allowed types: dropdown shown (includes "None" option when 'none' is allowed)
 */
import type { FC } from 'react'
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Database } from 'lucide-react'
import { AnalysisResultsConfig } from '@/subsystems/datasources'
import { SectionHeader } from '../common/SectionHeader'
import type {
  AnalysisDatasourceType,
  AnalysisResultsDatasourceSettings,
  ConnectorDatasourceSettings,
  FullTextDatasourceSettings,
} from '../../types'

const TYPE_LABELS: Record<Exclude<AnalysisDatasourceType, 'none'>, string> = {
  full_text: 'Full Text',
  connector: 'Connector',
  analysis_results: 'Analysis Results',
}

interface DatasourceSectionProps {
  allowedTypes: AnalysisDatasourceType[]
  datasourceType: AnalysisDatasourceType | null | undefined
  datasourceConfig: FullTextDatasourceSettings | ConnectorDatasourceSettings | AnalysisResultsDatasourceSettings | null
  onTypeChange: (type: AnalysisDatasourceType | null) => void
  onConfigChange: (key: string, value: unknown) => void
  orgId?: string
  workspaceId?: string
  agentId?: string
}

export const DatasourceSection: FC<DatasourceSectionProps> = ({
  allowedTypes,
  datasourceType,
  datasourceConfig,
  onTypeChange,
  onConfigChange,
  orgId,
  workspaceId,
  agentId,
}) => {
  const canDisable = allowedTypes.includes('none')
  const realTypes = allowedTypes.filter((t): t is Exclude<AnalysisDatasourceType, 'none'> => t !== 'none')
  const showDropdown = allowedTypes.length > 1

  // If only one option exists, it's always the effective type.
  // Backend defaults null → full_text, so mirror that when 'none' isn't allowed.
  const effectiveType = allowedTypes.length === 1
    ? allowedTypes[0]
    : datasourceType ?? (canDisable ? null : 'full_text')

  return (
    <section>
      <SectionHeader
        icon={<Database className="h-5 w-5" />}
        title="Data Source"
        description="Configure where this node reads data from."
      />

      <div className="mt-4 space-y-4">
        {showDropdown && (
          <div className="space-y-2">
            <Label htmlFor="datasource-type">Source Type</Label>
            <Select
              value={effectiveType ?? 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  onTypeChange(null)
                } else {
                  onTypeChange(value as AnalysisDatasourceType)
                }
              }}
            >
              <SelectTrigger id="datasource-type" className="max-w-xs">
                <SelectValue placeholder="Select data source..." />
              </SelectTrigger>
              <SelectContent>
                {canDisable && (
                  <SelectItem value="none">None (upstream input)</SelectItem>
                )}
                {realTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Type-specific config forms */}
        {effectiveType === 'full_text' && (
          <FullTextConfigFields config={datasourceConfig as FullTextDatasourceSettings} onChange={onConfigChange} />
        )}

        {effectiveType === 'connector' && (
          <ConnectorConfigFields config={datasourceConfig as ConnectorDatasourceSettings} onChange={onConfigChange} />
        )}

        {effectiveType === 'analysis_results' && (
          <AnalysisResultsConfig
            config={datasourceConfig as AnalysisResultsDatasourceSettings}
            onChange={onConfigChange as any}
            orgId={orgId}
            workspaceId={workspaceId}
            agentId={agentId}
          />
        )}
      </div>
    </section>
  )
}

/** Connector datasource config fields. */
const ConnectorConfigFields: FC<{
  config: ConnectorDatasourceSettings | null
  onChange: (key: string, value: unknown) => void
}> = ({ config, onChange }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="connector-id">Connector ID</Label>
      <p className="text-xs text-muted-foreground">
        The connector to fetch documents from (e.g., maryland-bills, federal-bills).
      </p>
      <Input
        id="connector-id"
        value={config?.connectorId ?? ''}
        onChange={(e) => onChange('connectorId', e.target.value || null)}
        placeholder="e.g., maryland-bills"
        className="max-w-xs"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="document-type">Document Type</Label>
      <p className="text-xs text-muted-foreground">
        The type of document to retrieve from the connector (e.g., bill, summary).
      </p>
      <Input
        id="document-type"
        value={config?.documentType ?? ''}
        onChange={(e) => onChange('documentType', e.target.value || null)}
        placeholder="e.g., bill"
        className="max-w-xs"
      />
    </div>
  </>
)

/** Inline full-text config fields (kept here to avoid an extra component file). */
const FullTextConfigFields: FC<{
  config: FullTextDatasourceSettings | null
  onChange: (key: string, value: unknown) => void
}> = ({ config, onChange }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="source-org-id">Source Organization ID</Label>
      <p className="text-xs text-muted-foreground">
        Fetch documents from a different organization. Leave empty to use current org.
      </p>
      <Input
        id="source-org-id"
        value={config?.sourceOrgId ?? ''}
        onChange={(e) => onChange('sourceOrgId', e.target.value || null)}
        placeholder="Current organization"
        className="max-w-xs"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="source-ws-id">Source Workspace ID</Label>
      <p className="text-xs text-muted-foreground">
        Fetch documents from a different workspace. Leave empty to use current workspace.
      </p>
      <Input
        id="source-ws-id"
        value={config?.sourceWsId ?? ''}
        onChange={(e) => onChange('sourceWsId', e.target.value || null)}
        placeholder="Current workspace"
        className="max-w-xs"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="group-id">Group ID Filter</Label>
      <p className="text-xs text-muted-foreground">
        Filter by group ID (e.g., Workday REQ#). Leave empty for all groups.
      </p>
      <Input
        id="group-id"
        value={config?.groupId ?? ''}
        onChange={(e) => onChange('groupId', e.target.value || null)}
        placeholder="All groups"
        className="max-w-xs"
      />
    </div>
  </>
)
